import 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { Accordion, Banner, Link, useToast } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { faHand } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GenericFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/generic-form-fields/generic-form-fields';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { generateValidationSchema } from 'src/utils/validation';
import {
  ICopyrightForm,
  IDependentValue,
  IOrganization,
  IPublishingModel
} from '@publishing-engine/domain/models';
import { ajvResolver } from '@hookform/resolvers/ajv';
import {
  CustomFormFieldDefinition,
  FormFieldCheckbox,
  FormFieldPublishingModel
} from '@publishing-engine/domain/types/article';
import { ISubmissionFieldResponseObject } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';
import {
  CustomFormFieldComponent,
  FormFieldAgreementCheckbox
} from 'src/components/FormField';
import Divider from 'src/components/Divider';
import { useAuthorSubmitCopyrightForm } from 'src/api/commands';
import { SubmissionInfoCard } from 'src/components/SubmissionInfo';
import { CopyrightAndPublicationAgreement } from './CopyrightFormAgreementTerms';
import { fetchContentManagementSystemConfig } from 'src/api/queries';
import { useFormDraft } from 'src/api/mutations';
import { DeepPartial } from '@publishing-engine/domain/types';
import { useState } from 'react';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import ProtectedActionButton from '../../../../ProtectedAction/Button';

type ICopyrightFormFieldType = {
  ownership: IDependentValue<string>;
  type: string;
  typeReason?: string;
  waiver?: boolean;
  articleProcessingCharge?: boolean;
  submittingAuthorSignature?: string;
  authorsFullNames?: string[];
  agreement: boolean;
};

type IForm = {
  copyrightForm: ICopyrightFormFieldType;
  preference: { publishingModel?: IPublishingModel | null };
};

type Props = {
  organizationData: Pick<IOrganization, 'name'> | undefined;
  draftCopyrightForm?: DeepPartial<ICopyrightForm>;
  isEditingAllowed?: boolean;
};

export const CopyrightForm: React.FC<Props> = ({
  organizationData,
  draftCopyrightForm,
  isEditingAllowed
}) => {
  const {
    workflowResponse,
    fieldsResponse,
    submissionId,
    submissionContextId,
    refetchSubmission,
    organizationId
  } = useSubmissionData();
  const publishingModelFFD = fieldsResponse.fields[
    'PublishingModel'
  ] as ISubmissionFieldResponseObject<FormFieldPublishingModel>;
  const currentPublishingModel = publishingModelFFD?.value;
  const publishingModelValue = currentPublishingModel?.model;
  const { articleWorkflowId } = fieldsResponse;
  const [accordionIndex, setAccordionIndex] = useState<number[]>([]);
  const toast = useToast();

  const formDraftMutation = useFormDraft();

  const authorCopyrightSubmissionMutation = useAuthorSubmitCopyrightForm({
    submissionId,
    organizationId,
    submissionContextId,
    articleWorkflowId
  });

  const { data: cmsConfig } = useQuery({
    queryKey: ['submission-context-cms', submissionContextId, submissionId],
    queryFn: () => fetchContentManagementSystemConfig(submissionContextId!)
  });

  const authorCopyrightSubmissionForm =
    workflowResponse.forms.authorCopyrightSubmission;

  if (!authorCopyrightSubmissionForm) {
    return;
  }

  const copyrightFormFields = new GenericFormFields(
    authorCopyrightSubmissionForm
  );

  const config = {
    CopyrightFormOwner: copyrightFormFields.getFieldConfig(
      'CopyrightFormOwner'
    ) as CustomFormFieldDefinition,
    CopyrightFormWaiver: copyrightFormFields.getFieldConfig(
      'CopyrightFormWaiver'
    ) as CustomFormFieldDefinition,
    CopyrightFormType: copyrightFormFields.getFieldConfig(
      'CopyrightFormType'
    ) as CustomFormFieldDefinition,
    CopyrightFormProcessingCharge: copyrightFormFields.getFieldConfig(
      'CopyrightFormProcessingCharge'
    ) as CustomFormFieldDefinition,
    CopyrightFormTypeReason: copyrightFormFields.getFieldConfig(
      'CopyrightFormTypeReason'
    ) as CustomFormFieldDefinition,
    CopyrightFormSubscriptionAgreement: copyrightFormFields.getFieldConfig(
      'CopyrightFormSubscriptionAgreement'
    ) as CustomFormFieldDefinition,
    CopyrightFormOAAgreement: copyrightFormFields.getFieldConfig(
      'CopyrightFormOAAgreement'
    ) as CustomFormFieldDefinition,
    CopyrightFormAuthorsFullNames: copyrightFormFields.getFieldConfig(
      'CopyrightFormAuthorsFullNames'
    ) as CustomFormFieldDefinition,
    CopyrightFormAuthorSignature: copyrightFormFields.getFieldConfig(
      'CopyrightFormAuthorSignature'
    ) as CustomFormFieldDefinition
  };

  const schema = generateValidationSchema(config);
  const methods = useForm<IForm>({
    defaultValues: {
      copyrightForm: draftCopyrightForm ? draftCopyrightForm : {},
      preference: { publishingModel: currentPublishingModel }
    },
    resolver: ajvResolver(schema)
  });
  const { handleSubmit, getValues, watch } = methods;

  const typeValue = watch('copyrightForm.type');

  const submitFormDraft = async () => {
    const formData = getValues();

    const response = await formDraftMutation.mutateAsync({
      entityId: submissionId,
      entityType: 'submission',
      formType: 'authorCopyrightSubmission',
      formSchemaId:
        workflowResponse.articleWorkflow.formSchemas.authorCopyrightSubmission!,
      data: formData.copyrightForm
    });

    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: 'Draft Saved',
        isClosable: true
      });
    } else {
      toast({
        variant: 'error',
        title: 'Draft Save Unsuccessful',
        description:
          'We encountered an issue while saving your draft. Please try again.',
        isClosable: true
      });
    }
  };

  const submit = async () => {
    const { copyrightForm } = getValues();

    const response = await authorCopyrightSubmissionMutation.mutateAsync({
      copyrightForm
    });

    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: 'Copyright and Publication Agreement Concluded',
        description: (
          <span>
            You will receive a confirmation email with a<br /> copy of the
            agreement after submission.
          </span>
        ),
        isClosable: true
      });
      refetchSubmission();
    } else {
      toast({
        variant: 'error',
        title: 'Copyright and Publication Agreement Conclusion Failed',
        description: 'Please try again later.'
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submit, scrollToFirstError)}>
        <Box>
          <Flex flexDirection="column" gap={8}>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingSection">
                Copyright and Publication Agreement
              </Text>
              <Text textStyle="textBody">
                {organizationData?.name && (
                  <>{organizationData.name} agrees to publish:</>
                )}
              </Text>
            </Flex>
            <Flex
              flexDirection="column"
              padding={6}
              borderRadius="base"
              background="neutralPrimaryFill"
            >
              <SubmissionInfoCard />
            </Flex>

            {cmsConfig?.copyrightPublicationTerms && (
              <Accordion
                data-testId="copyright-and-publication-terms"
                chakraAccordionProps={{
                  index: accordionIndex,
                  onChange: index =>
                    setAccordionIndex(Array.isArray(index) ? index : [index])
                }}
                accordionItemProps={{
                  background: 'neutralPrimaryContentInverse',
                  marginBottom: 0,
                  padding: 2
                }}
                items={[
                  {
                    title: 'Copyright and Publication Agreement Terms',
                    content: (
                      <CopyrightAndPublicationAgreement
                        markdownString={cmsConfig.copyrightPublicationTerms}
                      />
                    )
                  }
                ]}
              />
            )}

            <Flex flexDirection="column" gap={8}>
              <Banner
                heading="For your attention"
                content={
                  <>
                    Make sure to read the{' '}
                    <Link
                      variant="secondary"
                      onClick={() => setAccordionIndex([0])}
                    >
                      Copyright and Publication Agreement
                    </Link>{' '}
                    before selecting any options below.
                  </>
                }
                customIcon={<FontAwesomeIcon icon={faHand} />}
                variant="attention"
              />
              {config.CopyrightFormOwner && (
                <Flex
                  flexDirection="column"
                  paddingTop={3}
                  paddingX={6}
                  paddingBottom={6}
                  borderRadius="base"
                  background="neutralPrimaryFill"
                >
                  <CustomFormFieldComponent
                    config={config.CopyrightFormOwner}
                    isEditingAllowed={isEditingAllowed}
                  />
                </Flex>
              )}
              {(config.CopyrightFormWaiver ||
                config.CopyrightFormProcessingCharge ||
                config.CopyrightFormType) && (
                <Flex
                  flexDirection="column"
                  padding={6}
                  borderRadius="base"
                  background="neutralPrimaryFill"
                  gap={1}
                >
                  <Flex flexDirection="column">
                    <Text textStyle="headingItem">Publishing Model</Text>
                    <Text textStyle="textBody">
                      The selected publishing model for this Submission is{' '}
                      {
                        publishingModelFFD?.formFieldDefinition.params.labels
                          .options[publishingModelValue!]?.main!
                      }
                      .
                    </Text>
                  </Flex>
                  {config.CopyrightFormWaiver && (
                    <CustomFormFieldComponent
                      config={config.CopyrightFormWaiver}
                      isEditingAllowed={isEditingAllowed}
                    />
                  )}
                  {config.CopyrightFormProcessingCharge && (
                    <CustomFormFieldComponent
                      config={config.CopyrightFormProcessingCharge}
                      isEditingAllowed={isEditingAllowed}
                    />
                  )}
                  {config.CopyrightFormType && (
                    <CustomFormFieldComponent
                      config={config.CopyrightFormType}
                      isEditingAllowed={isEditingAllowed}
                    />
                  )}
                </Flex>
              )}
              {config.CopyrightFormTypeReason && typeValue === 'other' && (
                <Flex
                  flexDirection="column"
                  paddingTop={3}
                  paddingX={6}
                  paddingBottom={6}
                  borderRadius="base"
                  background="neutralPrimaryFill"
                  gap={4}
                >
                  <CustomFormFieldComponent
                    config={config.CopyrightFormTypeReason}
                    placeholder="State a reason"
                    isEditingAllowed={isEditingAllowed}
                  />
                </Flex>
              )}
              {(config.CopyrightFormAuthorSignature ||
                config.CopyrightFormOAAgreement ||
                config.CopyrightFormSubscriptionAgreement ||
                config.CopyrightFormAuthorsFullNames) && (
                <Flex
                  flexDirection="column"
                  paddingTop={3}
                  paddingX={6}
                  paddingBottom={6}
                  borderRadius="base"
                  background="neutralPrimaryFill"
                  gap={6}
                >
                  {config.CopyrightFormAuthorSignature && (
                    <CustomFormFieldComponent
                      config={config.CopyrightFormAuthorSignature}
                      placeholder="Enter your full name"
                      isEditingAllowed={isEditingAllowed}
                    />
                  )}
                  {config.CopyrightFormAuthorsFullNames && (
                    <Flex flexDirection="column" gap={6}>
                      <CustomFormFieldComponent
                        config={config.CopyrightFormAuthorsFullNames}
                        placeholder="Enter full name of author"
                        isEditingAllowed={isEditingAllowed}
                      />
                      {Divider}
                    </Flex>
                  )}
                  {config.CopyrightFormSubscriptionAgreement && (
                    <FormFieldAgreementCheckbox
                      config={
                        config.CopyrightFormSubscriptionAgreement as FormFieldCheckbox
                      }
                      isEditingAllowed={isEditingAllowed}
                    />
                  )}
                  {config.CopyrightFormOAAgreement && (
                    <FormFieldAgreementCheckbox
                      config={
                        config.CopyrightFormOAAgreement as FormFieldCheckbox
                      }
                      isEditingAllowed={isEditingAllowed}
                    />
                  )}
                  <Banner
                    heading="For your attention"
                    content={`By typing your name above and clicking “Confirm”,  
                      you will be assumed to have read and understood all of the terms and conditions of the Copyright and Publication Agreement 
                      and you will be agreeing to the terms of the ${
                        publishingModelValue === 'subscription'
                          ? 'assignment or licence (as the case may be) detailed above.'
                          : 'licence detailed above.'
                      }`}
                    customIcon={<FontAwesomeIcon icon={faHand} />}
                    variant="attention"
                  />
                </Flex>
              )}
            </Flex>
          </Flex>
        </Box>
        <Flex
          padding={6}
          bottom={0}
          justifyContent="center"
          position="sticky"
          bgColor="surfacePrimaryFill"
        >
          <Flex width="100%" justifyContent="flex-end" gap={4}>
            <ProtectedActionButton
              relation="author"
              entityType="submission"
              entityId={submissionId}
              variant="secondary"
              onClick={submitFormDraft}
              tooltipLabel={
                <span>You do not have permission to perform this action</span>
              }
            >
              Save Draft
            </ProtectedActionButton>
            <ProtectedActionButton
              relation="author"
              entityType="submission"
              entityId={submissionId}
              variant="primary"
              type="submit"
              tooltipLabel={
                <span>You do not have permission to perform this action</span>
              }
            >
              Confirm
            </ProtectedActionButton>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};
